import React from 'react';

import { Box, Card, CardActionArea, CardContent, Stack, Tooltip, Typography } from '@mui/material';

import { SocieteCohort, summariseSociete } from '~/utils/businessTraveller/societeOverview';

const tiles: Array<{ key: SocieteCohort; label: string; description: string; needsMetadata?: boolean }> = [
  { key: 'enrolled', label: 'Enrolled in Société', description: 'Active memberships at any tier.' },
  {
    key: 'not-enrolled',
    label: 'Not enrolled',
    description: 'Linked accounts without a Société membership in daily reporting.',
  },
  {
    key: 'one-away',
    label: '1 booking to Silver',
    description:
      'Travellers who need one more completed booking within their current qualification window to qualify for Silver. Includes unenrolled travellers.',
    needsMetadata: true,
  },
  {
    key: 'two-three-away',
    label: '2–3 bookings to Silver',
    description:
      'Travellers who need two or three more completed bookings within their current qualification window to qualify for Silver. Includes unenrolled travellers.',
    needsMetadata: true,
  },
  {
    key: 'activation-needed',
    label: 'Qualified · activation needed',
    description: 'Booking requirement met, but Société membership still needs activation.',
    needsMetadata: true,
  },
  {
    key: 'granted',
    label: 'Silver granted through LEBT',
    description: 'Travellers with a recorded successful LEBT Silver grant, including those now at a higher tier.',
    needsMetadata: true,
  },
];

interface Props {
  summary: ReturnType<typeof summariseSociete>;
  available: boolean;
  metadataReady: boolean;
  metadataFailed: boolean;
  selected: SocieteCohort | null;
  onSelect: (cohort: SocieteCohort | null) => void;
}

export default function BusinessDashboardOverview({
  summary,
  available,
  metadataReady,
  metadataFailed,
  selected,
  onSelect,
}: Props) {
  return (
    <Stack gap={1.5}>
      <Box
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(6, 1fr)' }, gap: 2 }}
      >
        {tiles.map((tile) => {
          const ready = available && (!tile.needsMetadata || metadataReady);
          return (
            <Card
              key={tile.key}
              variant="outlined"
              sx={{
                borderColor: selected === tile.key ? 'primary.main' : 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Tooltip title={tile.description}>
                <CardActionArea
                  disabled={!ready}
                  aria-pressed={selected === tile.key}
                  onClick={() => onSelect(selected === tile.key ? null : tile.key)}
                  sx={{ height: '100%' }}
                >
                  <CardContent sx={{ px: 1.5, py: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="body2" color="text.secondary">
                      {tile.label}
                    </Typography>
                    <Typography variant="h5" mt={0.25}>
                      {ready
                        ? summary.cohorts[tile.key].size.toLocaleString()
                        : metadataFailed || !available
                          ? '—'
                          : '…'}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Tooltip>
            </Card>
          );
        })}
      </Box>
      {metadataFailed && (
        <Typography variant="body2" color="error">
          Unable to load all grant records or qualification settings. Reload to retry.
        </Typography>
      )}
    </Stack>
  );
}
